import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi, type AuthTokens, type AuthUser } from './authApi';

const REFRESH_TOKEN_KEY = 'quests_refresh_token';
const USER_KEY = 'quests_user';

interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Silently refreshes the access token. Returns false if refresh fails. */
  refresh: () => Promise<boolean>;
  /** Always returns the latest access token, even immediately after a refresh. */
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Avoid concurrent refresh calls
  const refreshingRef = useRef<Promise<boolean> | null>(null);
  // accessToken/logout callbacks need the latest token without retriggering effects
  const accessTokenRef = useRef<string | null>(null);

  const storeSession = useCallback((tokens: AuthTokens, authUser: AuthUser) => {
    accessTokenRef.current = tokens.accessToken;
    setAccessToken(tokens.accessToken);
    setUser(authUser);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authUser));
  }, []);

  const clearSession = useCallback(() => {
    accessTokenRef.current = null;
    setAccessToken(null);
    setUser(null);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const refresh = useCallback((): Promise<boolean> => {
    if (refreshingRef.current) return refreshingRef.current;
    const promise = (async () => {
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!storedRefresh) return false;
      try {
        const tokens = await authApi.refresh(storedRefresh);
        accessTokenRef.current = tokens.accessToken;
        setAccessToken(tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        return true;
      } catch {
        clearSession();
        return false;
      } finally {
        refreshingRef.current = null;
      }
    })();
    refreshingRef.current = promise;
    return promise;
  }, [clearSession]);

  // On mount: try to restore session from stored refresh token + user
  useEffect(() => {
    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser) as AuthUser);
      } catch {
        localStorage.removeItem(USER_KEY);
      }
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    storeSession(res, { userId: res.userId, email: res.email, displayName: res.displayName });
  }, [storeSession]);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    const res = await authApi.register(email, password, displayName);
    storeSession(res, { userId: res.userId, email: res.email, displayName: res.displayName });
  }, [storeSession]);

  const logout = useCallback(async () => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefresh && accessTokenRef.current) {
      try { await authApi.logout(accessTokenRef.current, storedRefresh); } catch { /* ignore */ }
    }
    clearSession();
  }, [clearSession]);

  const getToken = useCallback(() => accessTokenRef.current, []);

  const value = useMemo(
    () => ({ user, accessToken, loading, login, register, logout, refresh, getToken }),
    [user, accessToken, loading, login, register, logout, refresh, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * fetch wrapper that automatically adds the Authorization header and retries
 * once after a silent token refresh on 401.
 */
export async function authFetch(
  url: string,
  options: RequestInit,
  getToken: () => string | null,
  doRefresh: () => Promise<boolean>,
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(url, { ...options, headers });
  // 401/403 = normal expired-token rejection. 500 is included defensively: a
  // misbehaving authorizer can surface an expired token as a 500 rather than a
  // 403, and we never want a stale token to wedge every action until reload.
  if (res.status !== 401 && res.status !== 403 && res.status !== 500) return res;
  if (!token) return res; // no token to refresh — don't mask a genuine server 500

  const refreshed = await doRefresh();
  if (!refreshed) return res;

  const newToken = getToken();
  if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
  return fetch(url, { ...options, headers });
}
